import { supabase } from '../lib/supabase';
import { logger } from './loggingService';

export interface DesignSystemConfig {
  id?: string;
  projectId: string;
  userId: string;
  name: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  neutralColors: Record<string, string>;
  semanticColors: Record<string, string>;
  fontFamilyPrimary: string;
  fontFamilySecondary: string;
  fontScale: Record<string, string>;
  spacingScale: Record<string, string>;
  borderRadius: Record<string, string>;
  componentVariants: Record<string, any>;
  logoUrl?: string;
  faviconUrl?: string;
  brandAssets: Array<{
    name: string;
    url: string;
    type: string;
  }>;
}

export interface WireframeComponent {
  id: string;
  designSystemId: string;
  name: string;
  category: string;
  componentType: string;
  propsSchema: Record<string, any>;
  defaultProps: Record<string, any>;
  styleVariants: Array<Record<string, any>>;
  thumbnailUrl?: string;
  isCustom: boolean;
  createdAt: string;
}

export class DesignSystemService {
  /**
   * Create a new design system for a project
   */
  static async createDesignSystem(
    projectId: string,
    config: Omit<DesignSystemConfig, 'id' | 'projectId' | 'userId'>
  ): Promise<string> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('design_systems')
        .insert({
          project_id: projectId,
          user_id: user.id,
          name: config.name,
          primary_color: config.primaryColor,
          secondary_color: config.secondaryColor,
          accent_color: config.accentColor,
          neutral_colors: config.neutralColors,
          semantic_colors: config.semanticColors,
          font_family_primary: config.fontFamilyPrimary,
          font_family_secondary: config.fontFamilySecondary,
          font_scale: config.fontScale,
          spacing_scale: config.spacingScale,
          border_radius: config.borderRadius,
          component_variants: config.componentVariants,
          logo_url: config.logoUrl,
          favicon_url: config.faviconUrl,
          brand_assets: config.brandAssets
        })
        .select('id')
        .single();

      if (error) throw error;

      logger.info('Design system created', { designSystemId: data.id, projectId });
      return data.id;
    } catch (error) {
      logger.error('Failed to create design system', { error, projectId });
      throw error;
    }
  }

  /**
   * Update an existing design system
   */
  static async updateDesignSystem(
    id: string,
    updates: Partial<DesignSystemConfig>
  ): Promise<void> {
    try {
      const updateData: any = {};
      
      if (updates.name) updateData.name = updates.name;
      if (updates.primaryColor) updateData.primary_color = updates.primaryColor;
      if (updates.secondaryColor) updateData.secondary_color = updates.secondaryColor;
      if (updates.accentColor) updateData.accent_color = updates.accentColor;
      if (updates.neutralColors) updateData.neutral_colors = updates.neutralColors;
      if (updates.semanticColors) updateData.semantic_colors = updates.semanticColors;
      if (updates.fontFamilyPrimary) updateData.font_family_primary = updates.fontFamilyPrimary;
      if (updates.fontFamilySecondary) updateData.font_family_secondary = updates.fontFamilySecondary;
      if (updates.fontScale) updateData.font_scale = updates.fontScale;
      if (updates.spacingScale) updateData.spacing_scale = updates.spacingScale;
      if (updates.borderRadius) updateData.border_radius = updates.borderRadius;
      if (updates.componentVariants) updateData.component_variants = updates.componentVariants;
      if (updates.logoUrl) updateData.logo_url = updates.logoUrl;
      if (updates.faviconUrl) updateData.favicon_url = updates.faviconUrl;
      if (updates.brandAssets) updateData.brand_assets = updates.brandAssets;

      const { error } = await supabase
        .from('design_systems')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      logger.info('Design system updated', { designSystemId: id });
    } catch (error) {
      logger.error('Failed to update design system', { error, designSystemId: id });
      throw error;
    }
  }

  /**
   * Get design system for a project
   */
  static async getDesignSystem(projectId: string): Promise<DesignSystemConfig | null> {
    try {
      const { data, error } = await supabase
        .from('design_systems')
        .select('*')
        .eq('project_id', projectId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // No rows found
        throw error;
      }

      return {
        id: data.id,
        projectId: data.project_id,
        userId: data.user_id,
        name: data.name,
        primaryColor: data.primary_color,
        secondaryColor: data.secondary_color,
        accentColor: data.accent_color,
        neutralColors: data.neutral_colors,
        semanticColors: data.semantic_colors,
        fontFamilyPrimary: data.font_family_primary,
        fontFamilySecondary: data.font_family_secondary,
        fontScale: data.font_scale,
        spacingScale: data.spacing_scale,
        borderRadius: data.border_radius,
        componentVariants: data.component_variants,
        logoUrl: data.logo_url,
        faviconUrl: data.favicon_url,
        brandAssets: data.brand_assets
      };
    } catch (error) {
      logger.error('Failed to get design system', { error, projectId });
      throw error;
    }
  }

  /**
   * Generate design system from blueprint
   */
  static async generateFromBlueprint(blueprintId: string): Promise<DesignSystemConfig> {
    try {
      // Get blueprint data
      const { data: blueprint, error: blueprintError } = await supabase
        .from('project_blueprints')
        .select('*')
        .eq('id', blueprintId)
        .single();

      if (blueprintError) throw blueprintError;

      // Use AI to generate design system based on blueprint
      const prompt = `
        Generate a design system configuration based on this blueprint:
        
        Project: ${blueprint.project_name}
        Features: ${JSON.stringify(blueprint.features)}
        Architecture: ${blueprint.architecture}
        Target Users: ${blueprint.target_users}
        
        Create a modern, professional design system with:
        - Appropriate color palette for the project type
        - Typography scale suitable for the application
        - Spacing system for consistent layouts
        - Component variants for the features described
        
        Return JSON with all design system properties.
      `;

      // Use AI service to generate the design system
      try {
        const { AnthropicService } = await import('./anthropicService');
        
        // Get userId from blueprint or current session
        let userId = blueprint.user_id;
        if (!userId) {
          const { data: { user } } = await supabase.auth.getUser();
          userId = user?.id;
        }
        
        if (!userId) {
          logger.warn('No userId available for design system generation, using default config');
          // Fall through to default config
        } else {
          const response = await AnthropicService.generateText(prompt, {
            maxTokens: 2000,
            temperature: 0.7,
            userId,
          });
          
          if (response.content) {
            try {
              // Try to parse AI response as JSON
              const aiConfig = JSON.parse(response.content);
              if (aiConfig && typeof aiConfig === 'object') {
                // Merge AI-generated config with required fields
                const generatedConfig: DesignSystemConfig = {
                  projectId: blueprint.project_id,
                  userId: blueprint.user_id || userId,
                  name: aiConfig.name || `${blueprint.project_name} Design System`,
                  primaryColor: aiConfig.primaryColor || aiConfig.primary_color || '#3B82F6',
                  secondaryColor: aiConfig.secondaryColor || aiConfig.secondary_color || '#10B981',
                  accentColor: aiConfig.accentColor || aiConfig.accent_color || '#F59E0B',
                  neutralColors: aiConfig.neutralColors || aiConfig.neutral_colors || {},
                  semanticColors: aiConfig.semanticColors || aiConfig.semantic_colors || {},
                  fontFamilyPrimary: aiConfig.fontFamilyPrimary || aiConfig.font_family_primary || 'Inter, sans-serif',
                  fontFamilySecondary: aiConfig.fontFamilySecondary || aiConfig.font_family_secondary || 'system-ui',
                  fontScale: aiConfig.fontScale || aiConfig.font_scale || {},
                  spacingScale: aiConfig.spacingScale || aiConfig.spacing_scale || {},
                  borderRadius: aiConfig.borderRadius || aiConfig.border_radius || {},
                  componentVariants: aiConfig.componentVariants || aiConfig.component_variants || {},
                  logoUrl: aiConfig.logoUrl || aiConfig.logo_url,
                  faviconUrl: aiConfig.faviconUrl || aiConfig.favicon_url,
                  brandAssets: aiConfig.brandAssets || aiConfig.brand_assets || []
                };
                
                logger.info('Design system generated from AI', { blueprintId });
                return generatedConfig;
              }
            } catch (parseError) {
              logger.warn('Failed to parse AI response as JSON, using default config', { error: parseError });
            }
          }
        }
      } catch (aiError) {
        logger.warn('AI design system generation failed, using default config', { error: aiError });
      }
      
      // Fallback: Return default configuration if AI generation fails
      const defaultConfig: DesignSystemConfig = {
        projectId: blueprint.project_id,
        userId: blueprint.user_id,
        name: `${blueprint.project_name} Design System`,
        primaryColor: '#3B82F6',
        secondaryColor: '#10B981',
        accentColor: '#F59E0B',
        neutralColors: {
          '50': '#F9FAFB',
          '100': '#F3F4F6',
          '200': '#E5E7EB',
          '300': '#D1D5DB',
          '400': '#9CA3AF',
          '500': '#6B7280',
          '600': '#4B5563',
          '700': '#374151',
          '800': '#1F2937',
          '900': '#111827'
        },
        semanticColors: {
          success: '#10B981',
          warning: '#F59E0B',
          error: '#EF4444',
          info: '#3B82F6'
        },
        fontFamilyPrimary: 'Inter, sans-serif',
        fontFamilySecondary: 'system-ui',
        fontScale: {
          xs: '0.75rem',
          sm: '0.875rem',
          base: '1rem',
          lg: '1.125rem',
          xl: '1.25rem',
          '2xl': '1.5rem',
          '3xl': '1.875rem',
          '4xl': '2.25rem'
        },
        spacingScale: {
          '0': '0',
          '1': '0.25rem',
          '2': '0.5rem',
          '3': '0.75rem',
          '4': '1rem',
          '6': '1.5rem',
          '8': '2rem',
          '12': '3rem',
          '16': '4rem'
        },
        borderRadius: {
          sm: '0.125rem',
          md: '0.375rem',
          lg: '0.5rem',
          xl: '0.75rem',
          '2xl': '1rem',
          full: '9999px'
        },
        componentVariants: {},
        brandAssets: []
      };

      logger.info('Design system generated from blueprint', { blueprintId });
      return defaultConfig;
    } catch (error) {
      logger.error('Failed to generate design system from blueprint', { error, blueprintId });
      throw error;
    }
  }

  /**
   * Upload brand asset
   */
  static async uploadBrandAsset(
    file: File,
    designSystemId: string,
    assetType: 'logo' | 'favicon' | 'image' | 'icon'
  ): Promise<string> {
    try {
      // Validate file type and size
      const allowedTypes = {
        logo: ['image/png', 'image/svg+xml', 'image/jpeg'],
        favicon: ['image/x-icon', 'image/png'],
        image: ['image/png', 'image/jpeg', 'image/webp'],
        icon: ['image/svg+xml', 'image/png']
      };

      if (!allowedTypes[assetType].includes(file.type)) {
        throw new Error(`Invalid file type for ${assetType}. Allowed: ${allowedTypes[assetType].join(', ')}`);
      }

      // Upload to Supabase Storage
      const path = `design-systems/${designSystemId}/${assetType}/${file.name}`;
      const { data, error } = await supabase.storage
        .from('brand-assets')
        .upload(path, file, { upsert: true });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('brand-assets')
        .getPublicUrl(path);

      // Update design system
      const updateField = assetType === 'logo' ? 'logo_url' : 
                         assetType === 'favicon' ? 'favicon_url' : 
                         'brand_assets';

      if (updateField === 'brand_assets') {
        // Add to brand_assets array
        const { data: currentSystem } = await supabase
          .from('design_systems')
          .select('brand_assets')
          .eq('id', designSystemId)
          .single();

        const brandAssets = currentSystem?.brand_assets || [];
        brandAssets.push({
          name: file.name,
          url: publicUrl,
          type: assetType
        });

        await supabase
          .from('design_systems')
          .update({ brand_assets: brandAssets })
          .eq('id', designSystemId);
      } else {
        await supabase
          .from('design_systems')
          .update({ [updateField]: publicUrl })
          .eq('id', designSystemId);
      }

      logger.info('Brand asset uploaded', { designSystemId, assetType, url: publicUrl });
      return publicUrl;
    } catch (error) {
      logger.error('Failed to upload brand asset', { error, designSystemId, assetType });
      throw error;
    }
  }

  /**
   * Process favicon to generate multiple sizes
   */
  static async processFavicon(file: File): Promise<{
    favicon16: string;
    favicon32: string;
    favicon180: string;
  }> {
    try {
      // This would use canvas API or image processing library
      // For now, return the same URL for all sizes
      const url = URL.createObjectURL(file);
      
      return {
        favicon16: url,
        favicon32: url,
        favicon180: url
      };
    } catch (error) {
      logger.error('Failed to process favicon', { error });
      throw error;
    }
  }

  /**
   * Get wireframe components for a design system
   */
  static async getWireframeComponents(designSystemId: string): Promise<WireframeComponent[]> {
    try {
      const { data, error } = await supabase
        .from('wireframe_components')
        .select('*')
        .eq('design_system_id', designSystemId)
        .order('category', { ascending: true });

      if (error) throw error;

      return data.map(component => ({
        id: component.id,
        designSystemId: component.design_system_id,
        name: component.name,
        category: component.category,
        componentType: component.component_type,
        propsSchema: component.props_schema,
        defaultProps: component.default_props,
        styleVariants: component.style_variants,
        thumbnailUrl: component.thumbnail_url,
        isCustom: component.is_custom,
        createdAt: component.created_at
      }));
    } catch (error) {
      logger.error('Failed to get wireframe components', { error, designSystemId });
      throw error;
    }
  }

  /**
   * Create a custom wireframe component
   */
  static async createWireframeComponent(
    designSystemId: string,
    component: Omit<WireframeComponent, 'id' | 'designSystemId' | 'createdAt'>
  ): Promise<string> {
    try {
      const { data, error } = await supabase
        .from('wireframe_components')
        .insert({
          design_system_id: designSystemId,
          name: component.name,
          category: component.category,
          component_type: component.componentType,
          props_schema: component.propsSchema,
          default_props: component.defaultProps,
          style_variants: component.styleVariants,
          thumbnail_url: component.thumbnailUrl,
          is_custom: component.isCustom
        })
        .select('id')
        .single();

      if (error) throw error;

      logger.info('Wireframe component created', { componentId: data.id, designSystemId });
      return data.id;
    } catch (error) {
      logger.error('Failed to create wireframe component', { error, designSystemId });
      throw error;
    }
  }
}
